// src/sections/privacy/SeedPhraseSetupModal.tsx
//
// SECURITY RULES for this file:
// 1. seedPhraseRef.current lives in useRef — NOT useState (React DevTools cannot inspect refs).
// 2. entropy is a local variable in async function — zeroed after use.
// 3. Server receives ONLY encrypted_master_key (ciphertext). NEVER seed phrase.
// 4. seedPhraseRef.current is cleared to "" after bootstrap completes or modal closes.
// 5. Password confirmation step is MANDATORY — ensures MK is encrypted with the known password.

import { useState, useRef, useEffect } from "react";
import { generateMnemonic, mnemonicToEntropy } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english.js";
import { Button, Text } from "@opal/components";
import { SvgShieldCheck, SvgClipboard, SvgCheck } from "@opal/icons";
import { MasterKeyManager } from "@/shared/lib/crypto/masterKey";
import { zkpaApi } from "@/shared/api/zkpa.api";
import { SeedPhraseGrid } from "./SeedPhraseGrid";
import { SeedPhraseConfirmStep } from "./SeedPhraseConfirmStep";
import { useAuthStore } from "@/shared/stores/authStore";

// "enter_password" → user must confirm their login password before seeing seed phrase
type Step = "enter_password" | "show" | "confirm" | "generating" | "done";

interface Props {
  open: boolean;
  userId: string;
  /** Login password from session — may be null if session was restored silently */
  password: string;
  onComplete: () => void;
}

export function SeedPhraseSetupModal({ open, userId, password, onComplete }: Props) {
  const [step, setStep] = useState<Step>("enter_password");
  const [copied, setCopied] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);

  // Password confirmation state
  const [enteredPassword, setEnteredPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  // The password that will actually be used to encrypt the MK
  // Set after password step is confirmed
  const verifiedPasswordRef = useRef<string>("");

  const setSessionPassword = useAuthStore((s) => s.setSessionPassword);

  // SECURITY: useRef, NOT useState — React DevTools cannot inspect ref values
  const seedPhraseRef = useRef<string>("");

  // Pre-fill password if we already know it from session
  useEffect(() => {
    if (open && password) {
      setEnteredPassword(password);
    }
  }, [open, password]);

  // Generate seed phrase when entering "show" step
  useEffect(() => {
    if (open && step === "show" && !seedPhraseRef.current) {
      seedPhraseRef.current = generateMnemonic(wordlist, 256);
    }
  }, [open, step]);

  // Clear all sensitive data when modal closes
  useEffect(() => {
    if (!open) {
      seedPhraseRef.current = "";
      verifiedPasswordRef.current = "";
      setStep("enter_password");
      setSetupError(null);
      setCopied(false);
      setEnteredPassword("");
      setConfirmPassword("");
      setPasswordError(null);
    }
  }, [open]);

  // ── Step: enter_password ─────────────────────────────────────────────────────
  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPasswordError(null);

    if (!enteredPassword) {
      setPasswordError("Vui lòng nhập mật khẩu đăng nhập.");
      return;
    }
    if (enteredPassword.length < 6) {
      setPasswordError("Mật khẩu phải có ít nhất 6 ký tự.");
      return;
    }
    if (enteredPassword !== confirmPassword) {
      setPasswordError("Hai mật khẩu không khớp. Vui lòng nhập lại.");
      return;
    }

    setVerifying(true);
    try {
      // Verify password is correct by calling /api/v1/auth/verify-password or
      // by checking against session password. If sessionPassword is set and differs,
      // the user entered the wrong password.
      if (password && password !== enteredPassword) {
        setPasswordError("Mật khẩu không khớp với phiên đăng nhập hiện tại.");
        setVerifying(false);
        return;
      }

      // Store verified password for use in bootstrap
      verifiedPasswordRef.current = enteredPassword;
      // Update session password store so unlock modal works immediately after
      setSessionPassword(enteredPassword);
      setStep("show");
    } catch {
      setPasswordError("Xác thực mật khẩu thất bại. Vui lòng thử lại.");
    } finally {
      setVerifying(false);
    }
  }

  // ── Step: confirm (seed phrase confirmed) ───────────────────────────────────
  async function handleConfirmComplete() {
    setStep("generating");
    setSetupError(null);

    try {
      const mnemonic = seedPhraseRef.current;
      const pwd = verifiedPasswordRef.current;

      if (!pwd) {
        throw new Error("Mật khẩu không hợp lệ — vui lòng bắt đầu lại.");
      }

      // Step 1: Convert mnemonic to entropy bytes (CLIENT ONLY — never sent to server)
      const entropy = mnemonicToEntropy(mnemonic, wordlist);

      // Step 2: Derive Master Key from entropy
      const masterKey = await MasterKeyManager.deriveKeyFromEntropy(new Uint8Array(entropy), userId);

      // Step 3: Encrypt MK with login key (password + PBKDF2)
      const { encryptedMasterKey, nonce, pbkdf2Salt } = await MasterKeyManager.encryptMasterKey(
        masterKey, pwd, userId
      );

      // Step 4: Generate Ed25519 key pair for signing
      const sigKeyPair = await crypto.subtle.generateKey(
        { name: "Ed25519" }, true, ["sign", "verify"]
      );
      const pubKeyRaw = new Uint8Array(await crypto.subtle.exportKey("raw", sigKeyPair.publicKey));
      const privKeyRaw = new Uint8Array(await crypto.subtle.exportKey("pkcs8", sigKeyPair.privateKey));

      // Step 5: Encrypt private key with Master Key
      const skNonce = crypto.getRandomValues(new Uint8Array(12));
      const mkRawForEncrypt = new Uint8Array(await crypto.subtle.exportKey("raw", masterKey));
      const tempMk = await crypto.subtle.importKey(
        "raw", mkRawForEncrypt, { name: "AES-GCM" }, false, ["encrypt"]
      );
      mkRawForEncrypt.fill(0); // zero immediately
      const encSK = new Uint8Array(
        await crypto.subtle.encrypt({ name: "AES-GCM", iv: skNonce }, tempMk, privKeyRaw)
      );

      // Step 6: POST to server — ONLY ciphertext (seed phrase and MK never sent)
      await zkpaApi.bootstrap({
        encrypted_master_key: encryptedMasterKey,
        master_key_nonce: nonce,
        encrypted_private_key: toBase64Url(encSK),
        private_key_nonce: toBase64Url(skNonce),
        public_key: toBase64Url(pubKeyRaw),
        pbkdf2_salt: pbkdf2Salt,
        pbkdf2_iterations: 200_000,
      });

      // Step 7: Zero sensitive local data
      entropy.fill(0);
      privKeyRaw.fill(0);
      encSK.fill(0);
      seedPhraseRef.current = "";

      // Step 8: Load MK into memory for current session
      const nonExtractableMk = await crypto.subtle.importKey(
        "raw",
        new Uint8Array(await crypto.subtle.exportKey("raw", masterKey)),
        { name: "AES-GCM" }, false, ["encrypt", "decrypt"]
      );
      const nonExtractableSk = await crypto.subtle.importKey(
        "pkcs8", new Uint8Array(await crypto.subtle.exportKey("pkcs8", sigKeyPair.privateKey)),
        { name: "Ed25519" }, false, ["sign"]
      );
      await MasterKeyManager.loadFromDerivedKey(
        nonExtractableMk,
        nonExtractableSk,
        sigKeyPair.publicKey,
        pubKeyRaw
      );

      setStep("done");
    } catch (err: any) {
      console.error("[ZKPA Bootstrap]", err?.code ?? err?.message ?? "unknown");
      setSetupError(err?.message ?? "Thiết lập thất bại — vui lòng thử lại");
      setStep("confirm");
    }
  }

  function handleCopy() {
    if (!seedPhraseRef.current) return;
    navigator.clipboard.writeText(seedPhraseRef.current);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  }

  const words = seedPhraseRef.current ? seedPhraseRef.current.split(" ") : [];

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div
      id="seed-phrase-setup-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="seed-setup-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.75)",
      }}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          color: "#111827",
          borderRadius: "12px",
          boxShadow: "0 25px 50px rgba(0,0,0,0.4)",
          width: "100%",
          maxWidth: step === "show" ? "680px" : "460px",
          margin: "0 16px",
          padding: "28px",
          maxHeight: "90vh",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
          <SvgShieldCheck style={{ width: "24px", height: "24px", color: "#2563eb", flexShrink: 0 }} aria-hidden="true" />
          <h2 id="seed-setup-title" style={{ fontSize: "18px", fontWeight: 700, margin: 0, color: "#111827" }}>
            {step === "enter_password" && "Xác nhận mật khẩu để thiết lập"}
            {step === "show" && "Lưu cụm từ khôi phục của bạn"}
            {step === "confirm" && "Xác nhận cụm từ khôi phục"}
            {step === "generating" && "Đang thiết lập bảo mật..."}
            {step === "done" && "Thiết lập hoàn tất! 🎉"}
          </h2>
        </div>

        {/* ── Step: enter_password ─────────────────────────────────────── */}
        {step === "enter_password" && (
          <form onSubmit={handlePasswordSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div
              style={{
                backgroundColor: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: "8px",
                padding: "10px 14px",
                fontSize: "13px",
                color: "#1e40af",
              }}
            >
              🔐 Mật khẩu đăng nhập sẽ được dùng để bảo vệ khoá bảo mật của bạn.
              Nhập đúng mật khẩu hiện tại để tiếp tục.
            </div>

            <div>
              <label htmlFor="setup-password" style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "5px", color: "#374151" }}>
                Mật khẩu đăng nhập
              </label>
              <input
                id="setup-password"
                type="password"
                autoFocus
                autoComplete="current-password"
                value={enteredPassword}
                onChange={(e) => { setEnteredPassword(e.target.value); setPasswordError(null); }}
                style={{
                  width: "100%", boxSizing: "border-box" as const,
                  border: "1px solid #d1d5db", borderRadius: "8px",
                  padding: "10px 14px", fontSize: "14px",
                  backgroundColor: "#f9fafb", color: "#111827", outline: "none",
                }}
                placeholder="••••••••"
              />
            </div>

            <div>
              <label htmlFor="setup-password-confirm" style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "5px", color: "#374151" }}>
                Xác nhận mật khẩu
              </label>
              <input
                id="setup-password-confirm"
                type="password"
                autoComplete="current-password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(null); }}
                style={{
                  width: "100%", boxSizing: "border-box" as const,
                  border: "1px solid #d1d5db", borderRadius: "8px",
                  padding: "10px 14px", fontSize: "14px",
                  backgroundColor: "#f9fafb", color: "#111827", outline: "none",
                }}
                placeholder="••••••••"
              />
            </div>

            {passwordError && (
              <div style={{
                backgroundColor: "#fef2f2", border: "1px solid #fecaca",
                borderRadius: "8px", padding: "10px 14px",
                fontSize: "13px", color: "#dc2626",
              }}>
                ⚠️ {passwordError}
              </div>
            )}

            <button
              id="setup-password-submit-btn"
              type="submit"
              disabled={verifying || !enteredPassword || !confirmPassword}
              style={{
                width: "100%", padding: "11px",
                background: verifying || !enteredPassword || !confirmPassword ? "#c7d2fe" : "#2563eb",
                color: "#fff", border: "none", borderRadius: "8px",
                fontSize: "15px", fontWeight: 600,
                cursor: verifying || !enteredPassword || !confirmPassword ? "not-allowed" : "pointer",
              }}
            >
              {verifying ? "Đang xác thực..." : "Tiếp tục →"}
            </button>
          </form>
        )}

        {/* ── Step: show ──────────────────────────────────────────────── */}
        {step === "show" && (
          <>
            <div className="space-y-4 py-2">
              <div
                className="bg-[var(--status-warning-00)] border border-[var(--status-warning-02)] rounded-md p-3"
                role="note"
                style={{ backgroundColor: "#fef3c7", border: "1px solid #fde68a", borderRadius: "8px", padding: "12px", marginBottom: "16px" }}
              >
                <Text color="default">
                  ⚠️ Đây là <strong>24 từ bí mật</strong> duy nhất để khôi phục dữ liệu.
                  Không ai — kể cả VNP — có thể giúp bạn nếu mất chúng.
                  Hãy ghi ra giấy hoặc lưu vào nơi an toàn.
                </Text>
              </div>

              {words.length === 24 && <SeedPhraseGrid words={words} />}

              <Button
                id="copy-seed-phrase-btn"
                variant="secondary"
                className="w-full flex items-center justify-center gap-2"
                onClick={handleCopy}
              >
                {copied
                  ? <><SvgCheck className="h-4 w-4" aria-hidden="true" />Đã sao chép!</>
                  : <><SvgClipboard className="h-4 w-4" aria-hidden="true" />Sao chép 24 từ</>
                }
              </Button>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                id="seed-phrase-saved-btn"
                variant="primary"
                className="w-full"
                onClick={() => setStep("confirm")}
              >
                Tôi đã lưu cụm từ này ✓
              </Button>
            </div>
          </>
        )}

        {/* ── Step: confirm ──────────────────────────────────────────── */}
        {step === "confirm" && (
          <div className="py-2">
            <SeedPhraseConfirmStep
              allWords={words}
              onConfirmed={handleConfirmComplete}
              onBack={() => setStep("show")}
              error={setupError}
            />
          </div>
        )}

        {/* ── Step: generating ───────────────────────────────────────── */}
        {step === "generating" && (
          <div
            style={{ textAlign: "center", padding: "48px 0" }}
            role="status"
            aria-live="polite"
          >
            <div
              className="animate-spin"
              style={{
                width: "44px", height: "44px",
                border: "3px solid #2563eb", borderTopColor: "transparent",
                borderRadius: "50%", margin: "0 auto 20px",
              }}
              aria-hidden="true"
            />
            <Text color="subtle">
              Đang thiết lập bảo mật... Vui lòng không tắt trình duyệt.
            </Text>
          </div>
        )}

        {/* ── Step: done ──────────────────────────────────────────────── */}
        {step === "done" && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔐</div>
            <Text color="default">Bảo mật đã được kích hoạt!</Text>
            <Text color="subtle">
              Dữ liệu của bạn từ giờ được mã hoá đầu cuối. Chỉ bạn mới có thể đọc.
            </Text>
            <Button
              id="zkpa-setup-done-btn"
              variant="primary"
              className="w-full mt-4"
              onClick={onComplete}
            >
              Bắt đầu sử dụng
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Local helper ──────────────────────────────────────────────────────────────
function toBase64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}
